import {
  type MappingTabCtx, type MappingTabArgs, type SchemaField, type SourceGroup,
  type SourceDef, type ProfileField,
  renderFieldRow, syncRowControls, collectFields, callFn, banner, escapeHtml,
  loadMappingProfile, toFieldSaveShape, toVariantSaveShape,
} from './otto-list-mapping.js';

// ============================================================================
// Otto-Lister — Reiter „Optionale Felder". Zeigt die kategorie-
// abhängigen Attribute (otto-listing-schema → group 'category_optional_attributes',
// Quelle otto.category_attributes) nach Otto-`attributeGroup` gruppiert + einklappbar.
// Default offen: nur Gruppen mit Pflicht-(LEGAL) oder empfohlen-(HIGH) Feldern; Toggle
// „alle anzeigen". Speichert ins SELBE Profil (otto_save_mapping_profile) und ERHÄLT dabei
// die Produktdaten-/Varianten-Felder des Mapping-Tabs (sonst würde das Replace-All sie löschen).
// ============================================================================

export async function renderOptionalFieldsTab(host: HTMLElement, ctx: MappingTabCtx, args: MappingTabArgs): Promise<void> {
  if (!args.ottoCategory) {
    host.innerHTML = banner('warn', 'Für dieses Listing ist keine OTTO-Kategorie gesetzt — optionale Felder sind kategoriegebunden.');
    return;
  }
  host.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade optionale Felder…</div>`;

  let schemaFields: SchemaField[];
  let flatSources: Array<{ group: string } & SourceDef>;
  let existing: Map<string, ProfileField>;
  let refreshPending = false;
  // Pflicht-(LEGAL-)Kategorie-Attribute leben jetzt im „Übersicht & Mapping"-Tab; hier nur die
  // empfohlenen/optionalen. requiredAttrKeys → beim Speichern die Pflicht-Attribute erhalten.
  const requiredAttrKeys = new Set<string>();
  try {
    const [schema, sources, profile] = await Promise.all([
      callFn<{ fields: SchemaField[]; refreshPending?: boolean }>(ctx, 'otto-listing-schema', { ottoCategory: args.ottoCategory }),
      callFn<{ groups: SourceGroup[] }>(ctx, 'vario-mapping-sources', { action: 'list', articleId: args.varioArticleId }),
      ctx.supabase.rpc('otto_get_mapping_profile', { _otto_category: args.ottoCategory }),
    ]);
    for (const f of schema.fields ?? []) {
      if (f.group === 'category_optional_attributes' && f.required) requiredAttrKeys.add(f.key);
    }
    schemaFields = (schema.fields ?? []).filter((f) => f.group === 'category_optional_attributes' && !f.required);
    refreshPending = schema.refreshPending === true;
    flatSources = (sources.groups ?? []).flatMap((g) =>
      g.sources.filter((s) => !s.isVariantAttribute).map((s) => ({ group: g.label, ...s })));
    const prof = (profile.data ?? null) as { fields?: ProfileField[] } | null;
    existing = new Map((prof?.fields ?? []).map((f) => [f.otto_field_key, f]));
  } catch (e) {
    host.innerHTML = banner('error', `Optionale Felder nicht ladbar: ${(e as Error).message}`);
    return;
  }

  if (schemaFields.length === 0) {
    host.innerHTML = banner('warn', refreshPending
      ? 'Noch keine Kategorie-Attribute geladen — bitte in den Einstellungen den Kategorie-Sync ausführen und erneut öffnen.'
      : 'Für diese Kategorie sind keine zusätzlichen (optionalen) Attribute hinterlegt.');
    return;
  }

  // Nach Otto-attributeGroup bündeln; innerhalb: Pflicht > empfohlen > Name.
  const byGroup = new Map<string, SchemaField[]>();
  for (const f of schemaFields) {
    const g = f.attributeGroup || 'Sonstige';
    const arr = byGroup.get(g) ?? [];
    arr.push(f);
    byGroup.set(g, arr);
  }
  const orderField = (a: SchemaField, b: SchemaField): number =>
    (Number(Boolean(b.required)) - Number(Boolean(a.required)))
    || (Number(Boolean(b.recommended)) - Number(Boolean(a.recommended)))
    || a.label.localeCompare(b.label);
  const groupNames = [...byGroup.keys()].sort((a, b) => a.localeCompare(b));
  const totalRec = schemaFields.filter((f) => f.recommended).length;

  host.innerHTML = `
    <div class="v-pa-md">
      <div class="otto-of-head">
        <h4 class="kl-sec-lab" style="margin:0;">Optionale Felder · ${escapeHtml(args.ottoCategory)}</h4>
        <div class="otto-of-actions">
          <label class="otto-of-toggle"><input type="checkbox" id="ofa-all"> alle anzeigen</label>
        </div>
      </div>
      <div class="otto-of-note">
        ${schemaFields.length} optionale Attribute · ${totalRec} empfohlen. Pflichtfelder dieser
        Kategorie stehen im Reiter „Übersicht &amp; Mapping". Gruppen aufklappen oder „alle anzeigen" nutzen.
      </div>
      <div id="ofa-groups"></div>
    </div>`;

  const groupsHost = host.querySelector<HTMLDivElement>('#ofa-groups')!;
  groupsHost.innerHTML = groupNames.map((name) => {
    const list = byGroup.get(name)!.slice().sort(orderField);
    const gReq = list.filter((f) => f.required).length;
    const gRec = list.filter((f) => f.recommended).length;
    const subParts = [gReq ? `${gReq} Pflicht` : '', gRec ? `${gRec} empfohlen` : ''].filter(Boolean);
    const subLabel = subParts.length ? ` · ${subParts.join(' · ')}` : '';
    // Beim Betreten alle Gruppen zugeklappt (.collapsed); „alle anzeigen" / Kopf-Klick expandiert.
    return `
      <div class="otto-ogroup collapsed" data-og-group>
        <div class="otto-ogroup-head" data-og-toggle>
          <span class="kl-chev v-icon fa-regular fa-chevron-down"></span>
          <span class="otto-og-title">${escapeHtml(name)}</span>
          <span class="otto-og-count">(${list.length}${subLabel})</span>
          ${gRec ? `<span class="otto-og-rec">${gRec} empfohlen</span>` : ''}
        </div>
        <div class="otto-ogroup-body">
          ${list.map((f) => renderFieldRow(f, existing.get(f.key), flatSources)).join('')}
        </div>
      </div>`;
  }).join('');

  // Pro Zeile: Wertcontrol je nach source_type ein-/ausblenden (auch in geschlossenen Gruppen).
  groupsHost.querySelectorAll<HTMLSelectElement>('[data-st-select]').forEach((sel) => {
    const sync = () => syncRowControls(groupsHost, sel.dataset['stSelect']!, sel.value);
    sel.addEventListener('change', sync);
    sync();
  });

  // Kopf-Klick klappt die einzelne Gruppe auf/zu (Chevron rotiert, Body via .collapsed verborgen).
  groupsHost.querySelectorAll<HTMLElement>('[data-og-toggle]').forEach((head) => {
    head.addEventListener('click', () => {
      head.closest('.otto-ogroup')?.classList.toggle('collapsed');
    });
  });

  host.querySelector<HTMLInputElement>('#ofa-all')?.addEventListener('change', (e) => {
    const open = (e.target as HTMLInputElement).checked;
    groupsHost.querySelectorAll<HTMLElement>('.otto-ogroup').forEach((g) => {
      g.classList.toggle('collapsed', !open);
    });
  });

  // Optionale-Felder speichern (liest DOM erst zur Ausführungszeit) — wird über die Save-Registry
  // des Detail-Panes vom globalen „Speichern"-Button ausgeführt (kein eigener Button mehr).
  const save = async (): Promise<void> => {
    const ownFields = collectFields(groupsHost, schemaFields); // group: category_optional_attributes (nur optionale)
    // Produktdaten/Varianten + die PFLICHT-Kategorie-Attribute (jetzt im Mapping-Tab gepflegt)
    // aus dem aktuellen Profil ERHALTEN (Replace-All würde sie sonst löschen).
    const cur = await loadMappingProfile(ctx, args.ottoCategory!);
    const preserved = cur.fields
      .filter((f) => f.field_group !== 'category_optional_attributes' || requiredAttrKeys.has(f.otto_field_key))
      .map(toFieldSaveShape);
    const variants = cur.variants.map(toVariantSaveShape);
    const { error } = await ctx.supabase.rpc('otto_save_mapping_profile', {
      _otto_category: args.ottoCategory, _fields: [...ownFields, ...preserved], _variants: variants,
    });
    if (error) throw new Error(error.message);
  };
  ctx.registerSaver?.('optional', save);
}
